import { makeStyles } from '@mui/styles';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFormSelector } from '../../custom-hooks';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { getIsRtlLanguage } from '../../selectors/AuthSelectors';
import {
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
  ZOOM_TYPES,
} from '../constants/form-manager.constants';
import { SubGridContext } from '../context';
import { getIsFormChangesMode } from '../selectors/common.selectors';
import { getChildrenWithProps, getEvaluatedConditions } from '../selectors/form-manager.selectors';
import { getIsChildSsZoomStandalone } from '../selectors/runtime.selectors';
import { equalityFn } from '../utils/form-manager.utils';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: ({
      isEditor,
      inTable,
      inSubGridOrMobile,
      isDetail,
      isTextBoxViewOnly,
      hasStandaloneZoom,
    }) =>
      !isEditor &&
      (((inTable || inSubGridOrMobile) && !isDetail) || isTextBoxViewOnly) &&
      !hasStandaloneZoom
        ? 'block'
        : 'flex',
    flexWrap: ({ isEditor }) => (isEditor ? 'wrap' : 'nowrap'),
    minWidth: ({ inTable }) => (inTable ? 184 : 0),
    '& input': {
      paddingRight: ({ childrenNodes, isRtlLanguage }) =>
        childrenNodes.length > 1 && !isRtlLanguage ? 35 : 8,
    },
    flexDirection: ({ isDetail, isViewOnly }) => (isDetail || isViewOnly ? 'column' : 'row'),
    gap: ({ isEditor, isFormChangesMode }) => (isEditor ? 10 : isFormChangesMode ? 16 : 0),
  },
  ssZoom: ({ inTable, inSubGridOrMobile, isEditor, isRtlLanguage, isFormChangesMode }) =>
    isFormChangesMode
      ? {
          marginTop: '0 !important',
        }
      : (inTable || inSubGridOrMobile) && !isEditor
      ? {
          position: 'absolute',
          top: 0,
          right: isRtlLanguage ? undefined : 0,
          zIndex: 10,
          left: isRtlLanguage ? 0 : undefined,
          marginLeft: isRtlLanguage ? -30 : undefined,
        }
      : {
          marginTop: 51,
          marginLeft: -42,
          zIndex: 10,
        },
  ssTextBox: {
    width: '100%',
    maxWidth: 568,
    flex: 1,
  },
  ssTextView: {
    marginTop: ({ inTable, inSubGridOrMobile }) => (inTable || inSubGridOrMobile ? 0 : 50),
    marginLeft: ({ inTable, inSubGridOrMobile, shouldAlignTextView }) =>
      shouldAlignTextView ? 12 : inTable || inSubGridOrMobile ? 0 : 18,
    flex: 1,
  },
  ssHyperLink: {
    marginTop: 3,
  },
});

const getClassName = (type) => (ZOOM_TYPES.includes(type) ? SS_CONTROL_TYPE.ZOOM : type);

function SSAdvancedTextBox({
  children,
  childrenNodes,
  inTable,
  isEditor,
  isDetail,
  isViewOnly,
  inFieldGroup,
  isHiddenLabel,
  index,
  id,
  isOnline,
}) {
  const { isMobileDevice } = useWindowDimensions();
  const [isAutoCompleteLoading, setIsAutoCompleteLoading] = useState(false);
  const childrenWithProps = useFormSelector(
    getChildrenWithProps,
    {
      childrenNodes,
      childrenProps: [
        FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
        FORM_MODEL_ATTRIBUTES.ENTITY_NAME,
        FORM_MODEL_ATTRIBUTES.CONTROL_ID,
        FORM_MODEL_ATTRIBUTES.FIELD_NAME,
      ],
    },
    equalityFn
  );
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const inSubGrid = useContext(SubGridContext);
  const hasStandaloneZoom = useFormSelector(getIsChildSsZoomStandalone, { id });
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);

  const { controlId, entityName, fieldName } =
    Object.values(childrenWithProps).find(
      ({ controlType }) => controlType === SS_CONTROL_TYPE.TEXT_BOX
    ) ?? {};

  const selectorProps = { id: controlId, index, entityName };

  const isTextBoxHidden = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.HIDDEN,
  });
  const isTextBoxViewOnly = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.VIEW_ONLY,
  });
  const isTextBoxRequired = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.REQUIRED,
  });

  const isAdvancedTextBoxHidden =
    isTextBoxHidden && !isTextBoxViewOnly && !isEditor && !isFormChangesMode;
  const inSubGridOrMobile = inSubGrid || isMobileDevice;

  const classes = useStyles({
    inTable,
    inSubGridOrMobile,
    isEditor,
    childrenNodes,
    isDetail,
    isTextBoxViewOnly,
    isViewOnly,
    shouldAlignTextView: isTextBoxRequired && inTable && !isTextBoxViewOnly,
    isRtlLanguage,
    hasStandaloneZoom,
    isFormChangesMode,
  });

  if (isAdvancedTextBoxHidden) {
    return null;
  }

  const commonChildren = [];
  const hyperLinkChildren = [];

  React.Children.forEach(children, (child) => {
    const type = childrenWithProps[child.props.id].controlType;
    const controlClassName = getClassName(type);

    const props = {
      className: classes[controlClassName],
      inTable,
      isViewOnly,
      inFieldGroup,
      isHiddenLabel,
      isDetail,
      index,
      isAutoCompleteLoading,
      setIsAutoCompleteLoading,
      isOnline,
      // stringify for memoization purpose
      textBoxPropsJson: JSON.stringify({
        entityName,
        textBoxId: controlId,
        fieldName,
        isTextBoxViewOnly,
        isTextBoxHidden,
      }),
    };

    if (type === SS_CONTROL_TYPE.HYPER_LINK) {
      hyperLinkChildren.push(React.cloneElement(child, props));
    } else {
      commonChildren.push(React.cloneElement(child, props));
    }
  });

  return (
    <div>
      <div className={classes.root}>{commonChildren}</div>
      {hyperLinkChildren.length > 0 && (
        <div className={classes.ssHyperLink}>{hyperLinkChildren}</div>
      )}
    </div>
  );
}

export default SSAdvancedTextBox;
